import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, EMPTY, map, switchMap, withLatestFrom } from 'rxjs';

import { BlazeTableService, BlazeTableStore } from '@blaze-dashboard/blaze-table-feature';
import { blazeActions } from '@blaze-shared/app-state-data-access';
import { ConnectorsTemplatesCollectionService } from '@blaze-shared/connectors-data-access';
import { BlazeTable } from '@blaze-shared/datasets-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { PanelService } from '@blaze-shared/shared-ui';

import { DataConnectorDialogComponent } from '../data-connector-dialog/data-connector-dialog.component';

@Injectable()
export class DataConnectorsEffects {
  private readonly actions$ = inject(Actions);
  private readonly connectorTemplatesCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly panelService = inject(PanelService);
  private readonly router = inject(Router);
  private readonly schemaService = inject(SchemaService);
  private readonly blazeTableStore = inject(BlazeTableStore);
  private readonly blazeTableService = inject(BlazeTableService);

  // Element properties can request creation of a connector if such is needed for the element to work properly.
  createConnector$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.createConnector),
        concatMap(({ template, connectorCreationRequest, redirectPath, projectId }) => {
          if (projectId && connectorCreationRequest) {
            connectorCreationRequest = { ...connectorCreationRequest, projectId };
          }
          return this.panelService
            .open(DataConnectorDialogComponent, {
              data: {
                action: 'create',
                connector: undefined,
                connectorCreationRequest,
                template,
              },
            })
            .pipe(
              switchMap((panel) => {
                if (!redirectPath) {
                  return EMPTY;
                }

                return panel
                  .afterClosed()
                  .pipe(switchMap(() => this.router.navigate([redirectPath])));
              }),
            );
        }),
      ),
    { dispatch: false },
  );

  // Element properties can request creation of a connector if such is needed for the element to work properly.
  editConnector$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.editConnector),
        withLatestFrom(this.connectorTemplatesCollService.entities$),
        map(([{ connector }, templates]) => {
          const template = templates.find(({ id }) => id === connector.templateId);
          return this.panelService.open(DataConnectorDialogComponent, {
            data: { action: 'editConnector', connector, template },
          });
        }),
      ),
    { dispatch: false },
  );

  editTable$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.editTable),
        switchMap(({ tableId }) => {
          return this.panelService.open(DataConnectorDialogComponent, {
            data: { action: 'editConnector', table: this.schemaService.getSchema(tableId) },
          });
        }),
      ),
    { dispatch: false },
  );

  removeColumn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.deleteColumn),
        map(({ columnId }) => {
          this.blazeTableStore.requestDeleteColumn(columnId);
        }),
      ),
    {
      dispatch: false,
    },
  );

  removeTable$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.deleteTable),
        map(({ tableId }) => {
          const table = this.schemaService.getSchema(tableId) as BlazeTable | undefined;
          const relatedTables = Array.from(this.schemaService.getSchemas().values()).filter(
            (schema) =>
              ('changeTracking' in schema && schema.changeTracking?.datasetId === tableId) ||
              (table && 'changeTracking' in table && schema.id === table.changeTracking?.datasetId),
          ) as BlazeTable[];
          if (!table) {
            return;
          }
          this.blazeTableService.deleteTableUI(table, relatedTables);
        }),
      ),
    {
      dispatch: false,
    },
  );
}
